import { createContext, ReactNode, useEffect, useState } from "react";
import { Notifications } from "../components/notification/Notifications";
import { mapAndFilterNulls } from "src/contexts/_shared/utils";
import { useMarkNotificationAsDelivered } from "../hooks/useMarkNotificationAsDelivered";
import { Notification } from "src/contexts/_shared/domain/Notification";
import { useDocumentationArea } from "../hooks/useDocumentationArea";
import { getNotificationInfoFromDocumentationAreaNotificationType } from "src/contexts/documentationArea/application/DocumentationAreaNotificationToNotification";
import { useGetCustomerCustomization } from "../hooks/useGetCustomization";
import { DocumentationAreaNotificationTypes } from "src/contexts/documentationArea/domain/DocumentationAreaNotification";
import { modelTypeToUploaderBoxTitle } from "src/contexts/docsAI/modelTypeToUploaderBoxTitle";
import { SUPPORTED_MODEL_TYPES } from "src/contexts/_shared/domain/SupportedModelTypes";
import {
  getMonthTranslationKeyByMonth,
  getPayslipMonthTranslationKeyByMonth,
} from "../routes/documentationArea/components/orderBox/header/OrderBoxHeaderTitle";

export type NotificationsContextType = {
  addNotifications: (notifications: Notification[]) => void;
  notifications: Notification[];
};

export const NotificationsContext = createContext<NotificationsContextType>(
  {} as NotificationsContextType,
);

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { documentationArea, selectedActorId } = useDocumentationArea();
  const { markNotificationAsDelivered } = useMarkNotificationAsDelivered();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const { notifications: fetchedCoreNotifications } = useDocumentationArea();
  const { isLoading } = useGetCustomerCustomization();

  const addNotifications = (newNotifications: Notification[]) => {
    const updatedNotifications = newNotifications.map((notification) => ({
      ...notification,
      onClose:
        notification.onClose ||
        ((id: string) => {
          setNotifications((ns) => ns.filter((n) => n.id !== id));
        }),
    }));
    setNotifications((prev) => [...prev, ...updatedNotifications]);
  };

  useEffect(() => {
    // Actor switch should not keep toasts from the previously selected actor.
    setNotifications([]);
  }, [selectedActorId]);

  useEffect(() => {
    if (isLoading) return;
    const newCoreNotifications = mapAndFilterNulls(fetchedCoreNotifications, (notification) => {
      if (notification.delivered) return null;

      const notificationInfo = getNotificationInfoFromDocumentationAreaNotificationType(
        notification.type,
      );

      let parsedMonth = notification.messageParams?.month;
      let parsedModelType = notification.messageParams?.modelType;

      if (
        parsedMonth &&
        notification.type === DocumentationAreaNotificationTypes.PAYSLIP_REQUEST_SUCCEEDED
      ) {
        parsedMonth = getPayslipMonthTranslationKeyByMonth(parsedMonth);
      }

      if (
        parsedMonth &&
        notification.type === DocumentationAreaNotificationTypes.MODEL_REQUEST_SUCCEEDED
      ) {
        parsedMonth = getMonthTranslationKeyByMonth(parsedMonth);
      }

      if (parsedModelType) {
        parsedModelType = modelTypeToUploaderBoxTitle[parsedModelType as SUPPORTED_MODEL_TYPES];
      }

      return {
        id: notification.id,
        title: notificationInfo.title,
        message: notification.message,
        messageParams: {
          ...notification.messageParams,
          month: parsedMonth,
          modelType: parsedModelType,
        },
        style: notificationInfo.style,
        onClose: (id: string) => {
          markNotificationAsDelivered({
            notificationId: id,
            documentationAreaId: documentationArea?.getId() ?? null,
            actorId: documentationArea?.getActor()?.getId() ?? null,
          });
          setNotifications((ns) => ns.filter((n) => n.id !== id));
        },
        createdAt: notification.createdAt,
      } as Notification;
    });

    setNotifications((prev) => {
      const existingFrontEndNotifications = prev.filter(
        (prevNotification) =>
          !newCoreNotifications.some((notification) => prevNotification.id === notification.id),
      );

      return [...existingFrontEndNotifications, ...newCoreNotifications];
    });
  }, [fetchedCoreNotifications, isLoading]);

  const value = {
    addNotifications,
    notifications,
  };

  return (
    <NotificationsContext.Provider value={value}>
      <Notifications />
      {children}
    </NotificationsContext.Provider>
  );
};
